import {
    NAVIGATION_KINDS,
    NAVIGATION_ROUTE_STRING_KEYS,
    OVERLAY_MAX_LAYERS,
} from "../api/navigation.generated";
import { samePagePath } from "../utils/authReturn";
import type {
    ActivityPartPayload,
    ApiBounds,
    ApiFileMetadata,
    ApiMessagePartKind,
    ApiModelReference,
    ApiReference,
    ArtifactMeta,
    CanonicalMessagePartPayload,
    ContextStatsEventData,
    MarvinAction,
    MarvinQuestion,
    MemoryNoticePartPayload,
    NavigationSuggestedEventData,
    NavigationSuggestionKind,
    ParsedApiMessagePart,
    ReferencePartPayload,
    TextPartPayload,
} from "./messagePartTypes";

export type {
    ActivityEntityRef,
    ActivityLabelRef,
    ActivityPartPayload,
    ActivityPlanGroup,
    ApiBounds,
    ApiFileMetadata,
    ApiMessagePart,
    ApiMessagePartKind,
    ApiCitedReference,
    ApiModelReference,
    ApiReference,
    ArtifactDisplayMode,
    ArtifactMeta,
    ArtifactRenderMode,
    AskUserOption,
    CanonicalMessagePartPayload,
    ContextStatsEventData,
    MarvinAction,
    MarvinActionFinalStatus,
    MarvinMemoryUpdate,
    MarvinNavigationSuggestion,
    MarvinQuestion,
    MemoryNoticePartPayload,
    NavigationSuggestedEventData,
    NavigationSuggestionKind,
    NavigationSuggestionMode,
    NavigationSuggestionRoute,
    ParsedApiMessagePart,
    ReferencePartPayload,
    TextPartPayload,
} from "./messagePartTypes";

type UnknownRecord = Record<string, unknown>;

const MESSAGE_PART_KIND: Record<ApiMessagePartKind, true> = {
    text: true,
    activity: true,
    reference: true,
    question: true,
    action: true,
    artifact: true,
    navigation: true,
    memory_notice: true,
    context_stats: true,
};

const NAVIGATION_KIND: Record<NavigationSuggestionKind, true> = Object.fromEntries(
    NAVIGATION_KINDS.map((kind) => [kind, true]),
) as Record<NavigationSuggestionKind, true>;

function record(value: unknown): UnknownRecord | null {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as UnknownRecord)
        : null;
}

function optionalString(value: unknown): boolean {
    return value === undefined || typeof value === "string";
}

function finiteOptionalNumber(value: unknown): boolean {
    return value === undefined || (typeof value === "number" && Number.isFinite(value));
}

function optionalRouteString(value: unknown): boolean {
    return value === undefined || (typeof value === "string" && value.trim().length > 0);
}

function requiredString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
}

/** Text, null or absent: a field the readers compare or `?.`-read but never require. */
function nullableOptionalString(value: unknown): boolean {
    return value === undefined || value === null || typeof value === "string";
}

/** Per-match highlight boxes (`ApiBounds.boxes`): absent, null, or `[xMin, yMin, xMax, yMax]`s. */
function validBoxes(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    return (
        Array.isArray(value) &&
        value.every(
            (box) =>
                Array.isArray(box) &&
                box.length === 4 &&
                box.every((edge) => typeof edge === "number" && Number.isFinite(edge)),
        )
    );
}

function parseApiBounds(value: unknown): ApiBounds | null {
    const candidate = record(value);
    if (!candidate) return null;
    const page = candidate.page;
    const xMin = candidate.xMin;
    const yMin = candidate.yMin;
    const xMax = candidate.xMax;
    const yMax = candidate.yMax;
    if (
        !Number.isSafeInteger(page) ||
        Number(page) < 0 ||
        typeof xMin !== "number" ||
        !Number.isFinite(xMin) ||
        typeof yMin !== "number" ||
        !Number.isFinite(yMin) ||
        typeof xMax !== "number" ||
        !Number.isFinite(xMax) ||
        typeof yMax !== "number" ||
        !Number.isFinite(yMax) ||
        xMin > xMax ||
        yMin > yMax ||
        (candidate.angle !== undefined &&
            (typeof candidate.angle !== "number" || !Number.isFinite(candidate.angle))) ||
        !validBoxes(candidate.boxes)
    ) {
        return null;
    }
    return value as ApiBounds;
}

export function parseApiReference(value: unknown): ApiReference | null {
    const candidate = record(value);
    if (
        !candidate ||
        !requiredString(candidate.fileId) ||
        !Array.isArray(candidate.sectionPath) ||
        !candidate.sectionPath.every((item) => typeof item === "string") ||
        !optionalRouteString(candidate.sectionUuid) ||
        !optionalRouteString(candidate.documentId) ||
        !optionalRouteString(candidate.sectionGroupId) ||
        !nullableOptionalString(candidate.documentType) ||
        !nullableOptionalString(candidate.documentSubtype) ||
        !nullableOptionalString(candidate.locatorQuality) ||
        !nullableOptionalString(candidate.folderPath)
    ) {
        return null;
    }
    const source = candidate.bounds;
    const serializedBounds = record(source);
    if (!(source instanceof Map) && !serializedBounds) return null;
    const values =
        source instanceof Map ? Array.from(source.values()) : Object.values(serializedBounds ?? {});
    if (values.some((bound) => parseApiBounds(bound) === null)) return null;
    return value as ApiReference;
}

export function parseApiModelReference(value: unknown): ApiModelReference | null {
    const candidate = record(value);
    const index = (item: unknown) =>
        item === undefined || (typeof item === "number" && Number.isSafeInteger(item) && item >= 0);
    if (
        !candidate ||
        candidate.kind !== "model" ||
        !requiredString(candidate.modelId) ||
        !requiredString(candidate.generationId) ||
        typeof candidate.label !== "string" ||
        !Array.isArray(candidate.sectionPath) ||
        !candidate.sectionPath.every((item) => typeof item === "string") ||
        !optionalString(candidate.isolate) ||
        !optionalString(candidate.select) ||
        !index(candidate.view) ||
        !index(candidate.level)
    ) {
        return null;
    }
    return value as ApiModelReference;
}

/**
 * A cited file, checked for every field the citation labels, icons and links read
 * (`referencePolicy`, `attachmentKind`, `DocumentTypeIcon`, `referenceHref`): one that would throw
 * there (a `fileType` of `{}`) costs its reference part here, before any reader sees it. An absent
 * one throws nowhere (each reader `?.`-reads it), so it costs nothing.
 */
function parseApiFile(value: unknown): ApiFileMetadata | null {
    const candidate = record(value);
    if (
        !candidate ||
        !requiredString(candidate.id) ||
        typeof candidate.fileName !== "string" ||
        !nullableOptionalString(candidate.fileType) ||
        !nullableOptionalString(candidate.fileUrl) ||
        !nullableOptionalString(candidate.originalFileUrl) ||
        !nullableOptionalString(candidate.documentType) ||
        !nullableOptionalString(candidate.documentSubtype) ||
        !optionalRouteString(candidate.documentId) ||
        !optionalRouteString(candidate.sectionGroupId)
    ) {
        return null;
    }
    return value as ApiFileMetadata;
}

/** 1..OVERLAY_MAX_LAYERS layers, each naming its own file and its exact revision. */
function validOverlayLayers(value: unknown): boolean {
    if (!Array.isArray(value) || value.length < 1 || value.length > OVERLAY_MAX_LAYERS) {
        return false;
    }
    return value.every((layer) => {
        const candidate = record(layer);
        return (
            !!candidate &&
            requiredString(candidate.fileId) &&
            requiredString(candidate.sectionGroupId)
        );
    });
}

function numericTarget(value: string, count: number): boolean {
    const fields = value.split(",").map(Number);
    if (fields.length !== count || fields.some((field) => !Number.isFinite(field))) {
        return false;
    }
    if (count === 5 && (!Number.isSafeInteger(fields[0]) || fields[0] < 0)) {
        return false;
    }
    const offset = count === 5 ? 1 : 0;
    return fields[offset] <= fields[offset + 2] && fields[offset + 1] <= fields[offset + 3];
}

/**
 * An overlay card persisted before the navigation contract carried `layers` named one second sheet in
 * `overlaySheet` (its file) and `overlaySectionGroupId` (its exact revision). Read it as the one-layer list it
 * meant, here at the decoding boundary, so nothing downstream sees two spellings. A legacy card with no revision
 * pin cannot name a contract layer and stays invalid.
 */
function normalizeLegacyOverlay(value: unknown): unknown {
    const candidate = record(value);
    const route = record(candidate?.route);
    if (
        !candidate ||
        !route ||
        candidate.kind !== "sheet_overlay" ||
        route.layers !== undefined ||
        !requiredString(route.overlaySheet) ||
        !requiredString(route.overlaySectionGroupId)
    ) {
        return value;
    }
    const { overlaySheet, overlaySectionGroupId, ...rest } = route;
    return {
        ...candidate,
        route: {
            ...rest,
            layers: [{ fileId: overlaySheet, sectionGroupId: overlaySectionGroupId }],
        },
    };
}

export function parseNavigationSuggestion(input: unknown): NavigationSuggestedEventData | null {
    const value = normalizeLegacyOverlay(input);
    const candidate = record(value);
    const route = record(candidate?.route);
    if (
        !candidate ||
        typeof candidate.kind !== "string" ||
        NAVIGATION_KIND[candidate.kind as NavigationSuggestionKind] !== true ||
        !requiredString(candidate.label) ||
        (candidate.mode !== undefined && candidate.mode !== "open" && candidate.mode !== "offer") ||
        !route
    ) {
        return null;
    }
    for (const key of NAVIGATION_ROUTE_STRING_KEYS) {
        if (!optionalRouteString(route[key])) return null;
    }
    // The app path the card opens, which the server builds (`navigation_href`): a path on this
    // origin wherever the app runs, never one a browser could read as another site (`/\host`).
    if (route.href !== undefined && (typeof route.href !== "string" || !samePagePath(route.href))) {
        return null;
    }
    if (route.layers !== undefined && !validOverlayLayers(route.layers)) return null;
    const kind = candidate.kind as NavigationSuggestionKind;
    const hasDocument = requiredString(route.documentId);
    const hasSectionGroup = requiredString(route.sectionGroupId);
    if (
        [
            "sheet",
            "sheet_region",
            "sheet_overlay",
            "section",
            "document",
            "submittal_register",
        ].includes(kind) &&
        !hasDocument
    ) {
        return null;
    }
    if (["sheet", "sheet_region", "sheet_overlay"].includes(kind) && !hasSectionGroup) {
        return null;
    }
    if ((kind === "section" || kind === "workbench") && !requiredString(route.nodeId)) return null;
    if (kind === "rfi" && !requiredString(route.rfiId)) return null;
    if (kind === "submittal" && !requiredString(route.submittalId)) return null;
    if (kind === "sheet_region" && (typeof route.hl !== "string" || !numericTarget(route.hl, 5))) {
        return null;
    }
    if (
        kind === "sheet_overlay" &&
        (route.layers === undefined ||
            (route.zoomTo !== undefined &&
                (typeof route.zoomTo !== "string" || !numericTarget(route.zoomTo, 4))))
    ) {
        return null;
    }
    return value as NavigationSuggestedEventData;
}

function parseArtifactMeta(value: unknown): ArtifactMeta | null {
    const candidate = record(value);
    if (
        !candidate ||
        !requiredString(candidate.id) ||
        !requiredString(candidate.filename) ||
        !requiredString(candidate.mimeType) ||
        (candidate.displayMode !== "inline" && candidate.displayMode !== "panel") ||
        (candidate.mode !== undefined && candidate.mode !== "inline" && candidate.mode !== "panel")
    ) {
        return null;
    }
    return value as ArtifactMeta;
}

function parseQuestion(value: unknown): MarvinQuestion | null {
    const candidate = record(value);
    if (
        !candidate ||
        typeof candidate.questionId !== "string" ||
        !candidate.questionId.trim() ||
        typeof candidate.question !== "string" ||
        !candidate.question.trim() ||
        !Array.isArray(candidate.options) ||
        !candidate.options.every((optionValue) => {
            const option = record(optionValue);
            return (
                option !== null &&
                typeof option.label === "string" &&
                option.label.trim().length > 0 &&
                (option.description === undefined ||
                    option.description === null ||
                    typeof option.description === "string")
            );
        }) ||
        typeof candidate.multiSelect !== "boolean" ||
        typeof candidate.allowOther !== "boolean" ||
        (candidate.status !== "pending" && candidate.status !== "answered") ||
        (candidate.answers !== undefined &&
            (!Array.isArray(candidate.answers) ||
                !candidate.answers.every((answer) => typeof answer === "string"))) ||
        (candidate.otherText !== undefined &&
            candidate.otherText !== null &&
            typeof candidate.otherText !== "string")
    ) {
        return null;
    }
    return value as MarvinQuestion;
}

function parseAction(value: unknown): MarvinAction | null {
    const candidate = record(value);
    if (
        !candidate ||
        typeof candidate.actionId !== "string" ||
        !candidate.actionId.trim() ||
        typeof candidate.actionType !== "string" ||
        !candidate.actionType.trim() ||
        typeof candidate.title !== "string" ||
        !candidate.title.trim() ||
        typeof candidate.summary !== "string" ||
        !record(candidate.payload) ||
        (candidate.status !== "pending" &&
            candidate.status !== "executed" &&
            candidate.status !== "failed" &&
            candidate.status !== "rejected" &&
            candidate.status !== "expired") ||
        (candidate.previewImageUrl !== undefined &&
            candidate.previewImageUrl !== null &&
            typeof candidate.previewImageUrl !== "string") ||
        (candidate.resultSummary !== undefined &&
            candidate.resultSummary !== null &&
            typeof candidate.resultSummary !== "string") ||
        (candidate.autoApproved !== undefined && typeof candidate.autoApproved !== "boolean") ||
        (candidate.result !== undefined && candidate.result !== null && !record(candidate.result))
    ) {
        return null;
    }
    return value as MarvinAction;
}

export function parseApiMessagePartPayload(
    kind: ApiMessagePartKind,
    value: unknown,
): CanonicalMessagePartPayload | null {
    const candidate = record(value);
    if (!candidate) return null;
    switch (kind) {
        case "text":
            return typeof candidate.text === "string" ? (value as TextPartPayload) : null;
        case "activity":
            return (candidate.kind === "thinking" ||
                candidate.kind === "tool" ||
                candidate.kind === "result") &&
                typeof candidate.text === "string" &&
                optionalString(candidate.toolName) &&
                optionalString(candidate.label) &&
                optionalString(candidate.source) &&
                optionalString(candidate.researchId) &&
                optionalString(candidate.helperId) &&
                optionalString(candidate.helperName) &&
                finiteOptionalNumber(candidate.contentOffset)
                ? (value as ActivityPartPayload)
                : null;
        case "reference": {
            const references = candidate.references;
            const entries =
                references instanceof Map
                    ? Array.from(references.values())
                    : Object.values(record(references) ?? {});
            if (
                (!(references instanceof Map) && !record(references)) ||
                entries.some(
                    (reference) =>
                        parseApiReference(reference) === null &&
                        parseApiModelReference(reference) === null,
                ) ||
                !Array.isArray(candidate.files) ||
                candidate.files.some((file) => parseApiFile(file) === null)
            ) {
                return null;
            }
            return value as ReferencePartPayload;
        }
        case "question":
            return parseQuestion(value);
        case "action":
            return parseAction(value);
        case "artifact":
            return parseArtifactMeta(value);
        case "navigation":
            return parseNavigationSuggestion(value);
        case "memory_notice":
            return (candidate.tier === "user" || candidate.tier === "org") &&
                typeof candidate.path === "string" &&
                candidate.path.trim().length > 0 &&
                optionalString(candidate.content) &&
                (candidate.contentRedacted === undefined ||
                    typeof candidate.contentRedacted === "boolean")
                ? (value as MemoryNoticePartPayload)
                : null;
        case "context_stats":
            return typeof candidate.conversationTokens === "number" &&
                Number.isFinite(candidate.conversationTokens) &&
                typeof candidate.pricingThreshold === "number" &&
                Number.isFinite(candidate.pricingThreshold) &&
                typeof candidate.contextWindow === "number" &&
                Number.isFinite(candidate.contextWindow) &&
                candidate.contextWindow > 0 &&
                finiteOptionalNumber(candidate.windowTokens) &&
                finiteOptionalNumber(candidate.inputTokens) &&
                finiteOptionalNumber(candidate.cachedTokens) &&
                finiteOptionalNumber(candidate.outputTokens) &&
                finiteOptionalNumber(candidate.memoryTokens) &&
                (candidate.hop === undefined ||
                    (Number.isSafeInteger(candidate.hop) && Number(candidate.hop) >= 0))
                ? (value as ContextStatsEventData)
                : null;
    }
}

export function parseApiMessagePart(value: unknown): ParsedApiMessagePart | null {
    const candidate = record(value);
    if (
        !candidate ||
        !Number.isSafeInteger(candidate.seq) ||
        Number(candidate.seq) < 0 ||
        typeof candidate.kind !== "string" ||
        MESSAGE_PART_KIND[candidate.kind as ApiMessagePartKind] !== true ||
        (candidate.status !== "streaming" && candidate.status !== "complete")
    ) {
        return null;
    }
    const payload = parseApiMessagePartPayload(
        candidate.kind as ApiMessagePartKind,
        candidate.payload,
    );
    if (!payload) return null;
    // A payload decoded into a newer spelling replaces the stored one; otherwise the part is returned as is.
    return (
        payload === candidate.payload ? value : { ...candidate, payload }
    ) as ParsedApiMessagePart;
}
